import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import locationService, {
  Location,
  UpdateLocationRequest,
} from '../../services/api/locationService';
import { PhoneInput, formatUSPhone, isValidUSPhone } from '../../components/ui/PhoneInput';
import { LocationForm, LocationFieldRow, LocationFormError, COUNTRY } from './LocationFormLayout';
import { CityAutocomplete } from './CityAutocomplete';
import { useStateOptions } from './useStateOptions';

interface EditFormValues {
  name: string;
  street: string;
  city: string;
  state: string;      // full state name submitted to backend
  stateCode: string;  // used for city lookup only, not submitted
  zip: string;
  phone: string;
}

interface EditLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: Location | null;
}

export const EditLocationModal = ({ isOpen, onClose, location }: EditLocationModalProps) => {
  const queryClient = useQueryClient();
  const { stateOptions, isLoading: statesLoading, resolveStateCode, resolveStateName } =
    useStateOptions();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<EditFormValues>();

  useEffect(() => {
    if (!location) return;
    reset({
      name: location.name,
      street: location.street ?? '',
      city: location.city,
      state: location.state,
      stateCode: resolveStateCode(location.state ?? ''),
      zip: location.zip ?? '',
      phone: formatUSPhone(location.phone ?? ''),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.id]);

  const stateCode = watch('stateCode');

  const updateMutation = useMutation({
    mutationFn: (data: UpdateLocationRequest) =>
      locationService.updateLocation(location!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['location', location?.id] });
      handleClose();
    },
  });

  const onSubmit = ({ stateCode: _, ...values }: EditFormValues) => {
    updateMutation.mutate({ ...values, country: COUNTRY });
  };

  const handleClose = () => {
    updateMutation.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Center"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={handleSubmit(onSubmit)}
            isLoading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </>
      }
    >
      <LocationForm onSubmit={handleSubmit(onSubmit)}>
        {updateMutation.isError && (
          <LocationFormError>
            {(updateMutation.error as any)?.response?.data?.message ||
              'Failed to update center. Please try again.'}
          </LocationFormError>
        )}

        <Input
          {...register('name', { required: 'Name is required' })}
          label="Center Name"
          placeholder="e.g. Phoenix North Clinic"
          error={errors.name?.message}
          required
          fullWidth
        />

        <Input
          {...register('street')}
          label="Street Address"
          placeholder="123 Main St"
          fullWidth
        />

        <LocationFieldRow>
          <Controller
            name="city"
            control={control}
            rules={{ required: 'City is required' }}
            render={({ field, fieldState }) => (
              <CityAutocomplete
                stateCode={stateCode ?? ''}
                value={field.value ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
                disabled={!stateCode}
              />
            )}
          />

          <Controller
            name="stateCode"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field, fieldState }) => (
              <Select
                label="State"
                fullWidth
                required
                disabled={statesLoading}
                options={stateOptions}
                value={field.value ?? ''}
                error={fieldState.error?.message}
                onChange={(e) => {
                  const code = e.target.value;
                  field.onChange(code);
                  setValue('state', resolveStateName(code));
                  setValue('city', '');
                }}
              />
            )}
          />
        </LocationFieldRow>

        <LocationFieldRow>
          <Input
            {...register('zip')}
            label="ZIP Code"
            placeholder="85001"
            fullWidth
          />

          <Input
            label="Country"
            value={COUNTRY}
            readOnly
            disabled
            fullWidth
          />
        </LocationFieldRow>

        <Controller
          name="phone"
          control={control}
          rules={{
            validate: (v) => isValidUSPhone(v) || 'Enter a valid 10-digit US phone number',
          }}
          render={({ field, fieldState }) => (
            <PhoneInput
              label="Phone Number"
              fullWidth
              value={field.value}
              error={fieldState.error?.message}
              onChange={field.onChange}
            />
          )}
        />
      </LocationForm>
    </Modal>
  );
};
